/*
 * Copyright (c) 2025 iPivot Corporation
 * All rights reserved.
 */
type OpaqueObject = Record<string, unknown>;

export interface Category {
  key: string;
  title: string;
  description: string;
  presentation: OpaqueObject;
};

export interface Supercat {
  key: string;
  title: string;
  description: string;
  presentation: OpaqueObject;
  internal: boolean;
};

export interface SupercatCount {
        supercat: string;
        category_count: number;
        finger_count: number;
  match_count: number;
}

export interface CategoryCount {
        key: string;
        finger_count: number;
  match_count: number;
}

export interface FingerMatchEntry {
  id: number;
  finger_id: number;
  ip: string;
  reverse_dns: string;
  forward_dns: string;
  recent_dns: string;
  tls_cert_subj: string;
  tls_sni: string;
  tls_alpn: string;
}

export interface Probe {
  probe_id: number;
  f_vendor: string;
  f_product: string;
  f_product_tag: string;
  f_vendor_tag: string;
  finger_id: number;
  m_ip: string;
  m_reverse_dns: string;
  m_forward_dns: string;
  m_recent_dns: string;
  m_tls_cert_subj: string;
  m_tls_sni: string;
  m_other: string;
  run_type: string;
  run_id: number;
  run_ts: number;
  probe_type: string;
  passed: boolean;
  op_name?: string;
  op_type?: string;
  title?: string;
  detail: object;
  children: Number[];
}

export interface Fingerprint {
    fid: number;
    pvkey: string;
    layer: number;
    product: string;
    product_tag: string;
    vendor: string;
    vendor_tag: string;
    title: string;
    description: string;
    presentation: object;
    res_type: string;
    res_tags: object;
    res_score: object;
    res_deco: object;
    categories: Category[];

    rpcerror?: string;   // if set, indicates an error fetching this fingerprint
    rpcstack?: string;   // stack trace if rpcerror is set
};

export interface FingerprintThin {
    fid: number;
    pvkey: string;
    layer: number;
    product: string;
    product_tag: string;
    vendor: string;
    vendor_tag: string;
    title: string;
    description: string;
    backend_selected: boolean;
    country_code: string;
    vendor_local: string;
    tag_count: number;
};

export interface ProbeFilter {
    pass?: string;
    run_id?: string;
    probe_type?: string;
};

const defaultProbe: Probes = {
    probes: [],
    run_ids: [],
    probe_types: []
};

// Define WebSocket interface for global object
declare global {
  var ws: {
    emit: (event: string, data: any, callback: (result: any) => void) => void;
  };
  var ws_waiters: Array<(value : unknown) => void>;
  var myWebsocket: {
    emit: (event: string, data: any, callback: (result: any) => void) => void;
  };
}

export class WsRpcError extends Error {
  cmd: string;
  rpcerror?: string;
  rpcstack?: string;
  rpcinfo?: any;

  constructor(cmd: string, rc: any) {
    super(`WS RPC Error: cmd=${cmd} ${rc?.rpcerror || 'Unknown RPC error'}`);
    this.name = 'WsRpcError';
    this.cmd = cmd;
    this.rpcerror = rc?.rpcerror;
    this.rpcstack = rc?.rpcstack;
    this.rpcinfo = rc?.rpcinfo;
  }
}

async function callWs<T>(cmd : string, args : any) : Promise<T> {
    while (!global.myWebsocket) {
      await new Promise(r => global.ws_waiters.push(r));
    }
    let rpcPayload: string;
    try {
      rpcPayload = JSON.stringify({ cmd, args });
    } catch (error : any) {
      console.error(`Failed to serialize WS RPC args for cmd=${cmd}:`, error);
      throw new Error(`Failed to serialize request for ${cmd}: ${error.message}`);
    }
    const MAX_PAYLOAD_SIZE = 8 * 1024 * 1024; // 8 MB
    const msgSize = new TextEncoder().encode(rpcPayload).length;
    if (msgSize > MAX_PAYLOAD_SIZE) {
      const err: any = new Error(`Payload size ${msgSize} exceeds 8 MB limit`);
      err.rpcinfo = { code: 'PAYLOAD_TOO_LARGE', byteSize: msgSize, limit: MAX_PAYLOAD_SIZE };
      throw new WsRpcError(cmd, err);
    }
    let rc : any = await new Promise((res, rej) => {
      try {
        global.myWebsocket.emit('rpc', { cmd, args }, (result) => res(result) );
      } catch (error) {
        rej(error);
      }
    });
    if (rc?.rpcerror) {
      throw new WsRpcError(cmd, rc);
    }
    return rc as T;
}

async function callHttp<T>(url: string, opts: RequestInit = {}): Promise<T> {
  const rc = await fetch(url, {
    credentials: 'include',
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(opts.headers || {})
    }
  });
  if (!rc.ok) {
    const txt = await rc.text();
    throw new Error(txt || `HTTP ${rc.status}`);
  }
  if (rc.status === 204) {
    return undefined as T;
  }
  return await rc.json() as T;
}

function parseDownloadFilename(contentDisposition: string | null): string {
  if (!contentDisposition) {
    return 'local-backup.sqlite';
  }
  const utfMatch = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utfMatch && utfMatch[1]) {
    try {
      return decodeURIComponent(utfMatch[1].trim());
    } catch {
      return utfMatch[1].trim();
    }
  }
  const plainMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
  if (plainMatch && plainMatch[1]) {
    return plainMatch[1].trim();
  }
  return 'local-backup.sqlite';
}

export async function downloadLocalDatabase(): Promise<void> {
  const rc = await fetch('/api/localdb/download', {
    method: 'GET',
    credentials: 'include',
    cache: 'no-store',
  });
  if (!rc.ok) {
    const txt = await rc.text();
    throw new Error(txt || `HTTP ${rc.status}`);
  }

  const fileBlob = await rc.blob();
  const fileName = parseDownloadFilename(rc.headers.get('content-disposition'));
  const href = URL.createObjectURL(fileBlob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(href);
}

export async function uploadLocalDatabase(file: File): Promise<{ ok: boolean; restarting?: boolean; message?: string; error?: string }> {
  const body = await file.arrayBuffer();
  const rc = await fetch('/api/localdb/upload', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/octet-stream',
    },
    body,
  });
  let payload: any = null;
  try {
    payload = await rc.json();
  } catch {
    payload = null;
  }
  if (!rc.ok) {
    const msg = payload?.error || payload?.message || `HTTP ${rc.status}`;
    throw new Error(msg);
  }
  return payload || { ok: true };
}

export interface SoundAssetEntry {
  id: string;
  fileName: string;
  label: string;
  url: string;
  local?: boolean;
}

export async function listLocalSoundAssets(): Promise<SoundAssetEntry[]> {
  const rc = await callHttp<{ ok: boolean; assets: SoundAssetEntry[] }>('/api/sound-assets', { method: 'GET' });
  return rc.assets || [];
}

export async function uploadLocalSoundAsset(file: File): Promise<{ ok: boolean; asset?: SoundAssetEntry; error?: string }> {
  const body = await file.arrayBuffer();
  const rc = await fetch('/api/sound-assets/upload', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'X-File-Name': encodeURIComponent(file.name),
    },
    body,
  });
  let payload: any = null;
  try {
    payload = await rc.json();
  } catch {
    payload = null;
  }
  if (!rc.ok) {
    const msg = payload?.error || payload?.message || `HTTP ${rc.status}`;
    throw new Error(msg);
  }
  return payload || { ok: true };
}

export async function deleteLocalSoundAsset(fileName: string): Promise<{ ok: boolean; fileName?: string; error?: string }> {
  const rc = await fetch(`/api/sound-assets/${encodeURIComponent(fileName)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  let payload: any = null;
  try {
    payload = await rc.json();
  } catch {
    payload = null;
  }
  if (!rc.ok) {
    const msg = payload?.error || payload?.message || `HTTP ${rc.status}`;
    throw new Error(msg);
  }
  return payload || { ok: true };
}

export interface AuthMe {
  ok: boolean;
  username: string;
  dir: string;
  role: string;
  capabilities: string[];
}

export async function authenticate(username: string, password: string): Promise<void> {
  await callHttp<void>('/auth/authenticate', {
    method: 'POST',
    body: JSON.stringify({ username, password })
  });
}

export async function authMe(): Promise<AuthMe> {
  return await callHttp<AuthMe>('/auth/me', { method: 'GET' });
}

export async function logout(): Promise<void> {
  await callHttp<void>('/auth/logout', { method: 'POST' });
}

export async function getFingerprints() : Promise<Fingerprint[]> {
  return callWs<Fingerprint[]>('getFingerprints', {});
}

export async function getFingerprintsFiltered(filter : FingerprintFilterParams) : Promise<Fingerprint[]> {
  return callWs<Fingerprint[]>('getFingerprintsFiltered', { filter });
}

function isPendingDeleteTag(value: unknown): boolean {
  if (value === true || value === 1 || value === '1') {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return false;
}

export async function getPendingDeleteFingerprints(): Promise<Fingerprint[]> {
  const rows = await getFingerprintsFiltered({
    tag: '$.yeet_pending'
  });
  return rows.filter((row) => isPendingDeleteTag((row.res_tags as Record<string, unknown> | undefined)?.yeet_pending));
}

export async function getFingerprintsThin() : Promise<FingerprintThin[]> {
  return callWs<FingerprintThin[]>('getFingerprintsThin', {});
}

export async function getFingerprintsThinFiltered(filter : FingerprintFilterParams) : Promise<FingerprintThin[]> {
  return callWs<FingerprintThin[]>('getFingerprintsThinFiltered', { filter });
}

export async function getFingerprint(id : number) : Promise<Fingerprint> {
    return callWs<Fingerprint>('getFingerprint', { finger_id: id });
}

export async function getFingerprintsByCategory(key: string) : Promise<Fingerprint[]> {
  return callWs<Fingerprint[]>('getFingerprintsByCategory', { key });
}

export async function getFingerprintsByCategoryThin(key: string) : Promise<FingerprintThin[]> {
  return callWs<FingerprintThin[]>('getFingerprintsByCategoryThin', { key });
}

/**
 * Fetch all probes associated with a fingerprint
 * @param fid Fingerprint id
 */
export async function getProbesForFinger(fid: number): Promise<Probe[]> {
  return callWs<Probe[]>('getProbesForFinger', { finger_id: fid });
}

/**
 * Fetch all matches for a fingerprint.
 * Alias / thin wrapper around existing getFingerMatches for naming consistency.
 * @param fid Fingerprint id
 */
export async function getMatchesForFinger(fid: number): Promise<FingerMatchEntry[]> {
  return callWs<FingerMatchEntry[]>('getMatchesForFinger', { finger_id: fid });
}

export async function setMatchesForFinger(fid: number, matches: FingerMatchEntry[]) : Promise<boolean> {
  return callWs<boolean>('setMatchesForFinger', { finger_id: fid, matches });
}

export interface FingerprintFilterParams {
  search?: string;
  allLayers?: boolean;
  layer?: number;
  selected?: string;  // 'Any', 'Selected', 'Unselected'
  tag?: string;
  tagValue?: string;
};

export interface GeoCommand {
  cmd?: string;       // 'add' or 'set' (locally used only)
  businessLocations: boolean;
  ipGeolocations: boolean;
  filter: FingerprintFilterParams;
}

// One record per distinct coordinate, folded server side.  n is the number of
// observations stacked there, nf the number of distinct fingerprints.
export interface GeoPoint {
  lat: number;
  lon: number;
  type: string;   // 'business' or 'ip'
  n: number;
  nf: number;
  fid: number;
  name?: string;  // vendor, set only on a point holding one company
}

export interface GeoBbox {
  minLat: number;
  minLon: number;
  maxLat: number;
  maxLon: number;
}

export interface GeoLeaf {
  fid: number;
  pvkey: string;
  layer: number;
  product: string;
  vendor: string;
  title: string;
  n: number;
  nbusiness: number;
  nip: number;
}

export interface GeoLeavesResult {
  total: number;
  observations: number;
  limit: number;
  offset: number;
  rows: GeoLeaf[];
}

export async function getFingerprintsGeo(args : GeoCommand): Promise<GeoPoint[]> {
  return callWs<GeoPoint[]>('getFingerprintsGeo', {
    filter: args.filter,
    businessLocations: args.businessLocations,
    ipGeolocations: args.ipGeolocations,
  });
}

// The fingerprints behind a clicked cluster, paged
export async function getFingerprintsGeoLeaves(args : {
  bbox: GeoBbox,
  businessLocations: boolean,
  ipGeolocations: boolean,
  filter: FingerprintFilterParams,
  limit?: number,
  offset?: number,
}): Promise<GeoLeavesResult> {
  return callWs<GeoLeavesResult>('getFingerprintsGeoLeaves', args);
}

export async function createFingerprint(): Promise<number> {
  return callWs<number>('createFingerprint', {});
}

export async function deleteFingerprint(id: number): Promise<boolean> {
  return callWs<boolean>('deleteFingerprint', { finger_id: id });
}

export async function setFingerprint(data: Fingerprint) : Promise<Fingerprint> {
  let odata = { ...data } as any;
  delete odata.matches;
  delete odata.probes;
  let rc = await callWs<Fingerprint>('setFingerprint', {
    finger_id: data.fid,
    finger_data: odata
  });
  return rc;
}

export async function getCategories(): Promise<Category[]> {
  return callWs<Category[]>('getCategories', {});
}

export async function getCategoriesBySupercatkey(supercatkey: string): Promise<Category[]> {
  return callWs<Category[]>('getCategoriesBySupercatkey', { supercatkey });
}

export async function getCategoryCountsBySupercatkey(supercatkey: string): Promise<CategoryCount[]> {
  return callWs<CategoryCount[]>('getCategoryCountsBySupercatkey', { supercatkey });
}

export async function getCategory(key: string): Promise<Category> {
  return callWs<Category>('getCategory', { key });
}

export async function setCategory(data: Category): Promise<Category> {
  return callWs<Category>('setCategory', data);
}

export async function deleteCategory(key: string): Promise<Category> {
  return callWs<Category>('deleteCategory', { key });
}

export async function getSchemas() : Promise<any> {
  return callWs<any>('getSchemas', {});
}

export async function clearBeSelection() : Promise<boolean> {
  return callWs<boolean>('clearSelection', {});
}

interface NumberIndexed {
  [index: number]: boolean;
}
export async function getBeSelection(): Promise<NumberIndexed> {
  let sel = await callWs<number[]>('getSelection', {});
  let res : NumberIndexed = {};
  sel.forEach((e : number) => res[e] = true);
  return res;
}

export async function setBeSelection(data: object): Promise<boolean> {
  return callWs<boolean>('setBeSelection', { selection: Object.keys(data).map((k) => parseInt(k)) });
}

export async function addToBeSelection(ids: number | number[]): Promise<void> {
  const list = Array.isArray(ids) ? ids : [ids];
  return callWs<void>('addToSelection', { finger_ids: list });
}

export async function removeFromBeSelection(ids: number | number[]): Promise<void> {
  const list = Array.isArray(ids) ? ids : [ids];
  return callWs<void>('removeFromSelection', { finger_ids: list });
}

// put all fingerprints into the working set
export async function beSelOpAll(clear: boolean): Promise<object> {
  return callWs<object>('beSelOpAll', { clear });
}

export async function beSelOpInvert(): Promise<object> {
  return callWs<object>('beSelOpInvert', {});
}

export async function addSelectionFromFilter(filter : any ): Promise<object> {
  return callWs<object>('selectionFromFilter', { filter, add: true });
}

export async function removeSelectionFromFilter(filter : any ): Promise<object> {
  return callWs<object>('selectionFromFilter', { filter, add: false });
}

export enum ProbeStatus {
    ANY_PASS = 'any',
    ALL_PASS = 'all',
    NONE_PASS = 'none',
    NO_PROBES = 'noprobe',
};

export async function beSelOpProbe(probeType: string, probeStatus: ProbeStatus, add: boolean): Promise<object> {
  return callWs<object>('beSelOpProbe', { probeType, probeStatus, add });
}

export async function beSelOpLayer(layer: number, add: boolean): Promise<object> {
  return callWs<object>('beSelOpLayer', { layer, add });
}

export async function beSelOpTag(tagpath: string, add: boolean, val: string | null): Promise<object> {
  return callWs<object>('beSelOpTag', { tagpath, add, val });
}

export async function tagWorkingSet(tagName : string, add: boolean): Promise<boolean> {
  return callWs<boolean>('tagWorkingSet', { tagName, add });
}

export interface DeleteWorkingSetCounts {
  invert: boolean;
  total: number;
  selected: number;
  fingerprints: number;
  remaining: number;
  matches: number;
  categories: number;
  probes: number;
}

// invert=false deletes the working set, invert=true deletes everything else
export async function previewDeleteWorkingSet(invert: boolean): Promise<DeleteWorkingSetCounts> {
  return callWs<DeleteWorkingSetCounts>('previewDeleteWorkingSet', { invert });
}

export async function deleteWorkingSet(invert: boolean): Promise<DeleteWorkingSetCounts> {
  return callWs<DeleteWorkingSetCounts>('deleteWorkingSet', { invert });
}
export async function exportJval(fileName: string): Promise<string> {
  return callWs<string>('exportJval', { fileName });
}

export interface FingerprintStatLayer {
  layer: number;
  count: number;
}
export interface FingerprintStatCategory {
  category: string
  count: number;
}
export interface ProbeStatRollupItem {
  run: number;
  type: string;
  probePassed: number;
  probeFraction: number;
  probeFailed: number;
  fingerprintsPassed: number;
  fingerprintsFraction: number;
  fingerprintsFailed: number;
}
export interface ProbeStat {
  probeCount: number;
  fingerCount: number;
  rollup: ProbeStatRollupItem[];
}
export interface FingerprintStat {
  bylayer: FingerprintStatLayer[];
  bycategory: FingerprintStatCategory[];
  probestats: ProbeStat;
}

export async function statDash(): Promise<FingerprintStat> {
  return callWs<FingerprintStat>('statDash', {});
}

export async function getSupercat(): Promise<Supercat[]> {
  return callWs<Supercat[]>('getSupercat', {});
}
export async function getSupercatCounts(): Promise<SupercatCount[]> {
  return callWs<SupercatCount[]>('getSupercatCounts', {});
}

export interface SampleWorkingSetArgs {
  count: number;
  method: 'random' | 'oldest';
  mode: 'replace' | 'augment';
  pattern?: string | null;
  minId?: number | null;
  maxId?: number | null;
  neverUpdated?: boolean | null;
}

export async function sampleWorkingSet(args: SampleWorkingSetArgs): Promise<number[]> {
  return callWs<number[]>('sampleWorkingSet', args);
}

export type WorkingSetExportRowType = 'fingerprints' | 'matches' | 'dns_fqdns';
export type WorkingSetExportFormat = 'json' | 'csv';

export interface WorkingSetExportSettings {
  rowType: WorkingSetExportRowType;
  format: WorkingSetExportFormat;
  fingerprintFields?: string[];
  includeFingerprintMatches?: boolean;
  includeFingerprintCategories?: boolean;
  matchFields?: string[];
  dnsFqdnFields?: string[];
}

export interface WorkingSetExportArgs {
  settings: WorkingSetExportSettings;
  action: 'preview' | 'download' | 'save';
  fileName?: string;
  cloud?: boolean;
  progressId?: string;
}

export interface WorkingSetExportResult {
  rowType: WorkingSetExportRowType;
  format: WorkingSetExportFormat;
  rowCount: number;
  fieldCount: number;
  fields: string[];
  byteSize: number;
  sampleRows?: any[];
  fileName?: string;
  mime?: string;
  content?: string;
  saved?: boolean;
  cloud?: boolean;
}

export async function exportWorkingSet(args: WorkingSetExportArgs): Promise<WorkingSetExportResult> {
  return callWs<WorkingSetExportResult>('exportWorkingSet', args);
}

export async function publishTsExtract() {
  return callWs<boolean>('publishTsExtract', {});
}

export async function getProbe(probeId: number): Promise<Probe> {
  return callWs<Probe>('getProbe', { probe_id: probeId });
}

export async function getProbeChildren(probeId: number): Promise<any[]> {
  return callWs<any[]>('getProbeChildren', { probe_id: probeId });
}

export interface ProbeRoot {
  id: number;
  probe_type: string;
  pending: boolean;
  passed: boolean;
}

export async function getProbeRoots(): Promise<ProbeRoot[]> {
  return callWs<ProbeRoot[]>('getProbeRoots', {});
}

export interface Probes {
  probes: Probe[];
  run_ids: number[];
  probe_types: string[];
}

export async function getProbes(filter : ProbeFilter): Promise<Probes> {
  if (filter.pass === 'all') {
    delete filter.pass;
  }
  if (filter.run_id === 'all') {
    delete filter.run_id;
  }
  if (filter.probe_type === 'all') {
    delete filter.probe_type;
  }
  return callWs<Probes>('getProbes', { filter });
}

export type ScanStatus = 'success' | 'failure';

export interface ScanResultRow {
  finger_id: number;
  pvkey: string;
  fqdn: string;
  ip_address: string | null;
  dns_scan: ScanStatus;
  tcp_scan: ScanStatus;
  curl_scan_80: ScanStatus;
  curl_scan_443: ScanStatus;
  fingerprint_passed: boolean;
}

export interface ScanResultsRollup {
  dns: { success: number; failure: number; };
  tcp: { success: number; failure: number; };
  curl80: { success: number; failure: number; };
  curl443: { success: number; failure: number; };
  fingerprints: { total: number; passed: number; failed: number; };
  total_rows: number;
}

export interface ScanResultsData {
  rows: ScanResultRow[];
  rollup: ScanResultsRollup;
}

export async function getScanResults(): Promise<ScanResultsData> {
  return callWs<ScanResultsData>('getScanResults', {});
}

export async function mergeLayers(sources: string, target: string): Promise<boolean> {
  return callWs<boolean>('mergeLayers', { sources, target });
}

export async function layerStats(): Promise<any> {
  return callWs<any>('layerStats', {});
}

export interface ProbeTopNResult {
    pending: any[];
    completed: any[];
    counts: {
        pending: number;
        completed: number;
    }
};

export async function probeTopN(n: number): Promise<any> {
  return callWs<ProbeTopNResult>('probeTopN', { n });
}

export async function queueCodeProbe(code: string): Promise<boolean> {
  return callWs<boolean>('queueCodeProbe', { code });
}

export async function queueAiProbe(opId: string, detail?: any): Promise<Number> {
  return callWs<Number>('queueAiProbe', { aiOpId: opId, detail });
}
export async function runAiProbe  (opId: string, detail?: any): Promise<Number> {
  return callWs<Number>('runAiProbe', { aiOpId: opId, detail });
}

export async function runAiProbeAsJob(opId: string, detail?: any): Promise<any> {
  return callWs<any>('runAiProbeAsJob', { aiOpId: opId, detail });
}

/**
 * Purges probes of a specific type or all probes
 * @param probeType The type of probe to purge, or 'ALL' to purge all probes
 * @returns The number of probes that were purged
 */
export async function purgeProbe(probeType: string = 'ALL', queueStatus: string = 'PENDING'): Promise<number> {
  return callWs<number>('purgeProbe', { probeType, queueStatus });
}

export async function getProbeTypes(): Promise<string[]> {
  return callWs<string[]>('getProbeTypes', {});
}

/**
 * Execute a control command on the server
 * @param cmd The command to execute
 * @returns Result of the command execution
 */
export async function execControl(cmd: string): Promise<any> {
  return callWs<any>('execControl', { cmd });
}

/**
 * Get available code templates
 * @returns Array of template strings
 */
export async function getCodeTemplates(): Promise<any[]> {
  return callWs<any[]>('getCodeTemplates', {});
}

export interface AiConnector {
  id: string;
  description: string;
  system_prompt: string;
  connection: Record<string, any>;
};

export type AiContext = AiConnector;

export interface AiOp {
  id: string;
  type: string;
  description: string;
  context_id: string;
  impl: string;
  main?: boolean;
  parent_op?: string | null;
  default_args?: Record<string, any>;
  group?: string | null;
  group_title?: string | null;
  group_folded?: boolean;
  context?: AiConnector;
  connector?: AiConnector;
};

export interface AiParser {
  name: string;
  description: string;
  input_schema: Record<string, any>;
};

export async function getAiOps(): Promise<AiOp[]> {
  return callWs<AiOp[]>('getAiOps', {});
}

export async function getAiOp(id: string): Promise<AiOp> {
  return callWs<AiOp>('getAiOp', { id });
}

export async function createAiOp(aiOp: AiOp): Promise<string> {
  return callWs<string>('createAiOp', { aiOp });
}

export async function setAiOp(aiOp: AiOp): Promise<boolean> {
  return callWs<boolean>('setAiOp', { aiOp });
}

export async function deleteAiOp(id: string): Promise<boolean> {
  return callWs<boolean>('deleteAiOp', { id });
}

export async function getAiConnectors(): Promise<AiConnector[]> {
  return callWs<AiConnector[]>('getAiConnectors', {});
}

export async function getAiConnector(id: string): Promise<AiConnector> {
  return callWs<AiConnector>('getAiConnector', { id });
}

export async function createAiConnector(aiConnector: AiConnector): Promise<string> {
  return callWs<string>('createAiConnector', { aiConnector });
}

export async function setAiConnector(aiConnector: AiConnector): Promise<void> {
  return callWs<void>('setAiConnector', { aiConnector });
}

export async function deleteAiConnector(id: string): Promise<boolean> {
  return callWs<boolean>('deleteAiConnector', { id });
}

export async function getAiContexts(): Promise<AiContext[]> {
  return getAiConnectors();
}

export async function getAiContext(id: string): Promise<AiContext> {
  return getAiConnector(id);
}

export async function createAiContext(aiContext: AiContext): Promise<string> {
  return createAiConnector(aiContext);
}

export async function setAiContext(aiContext: AiContext): Promise<void> {
  return setAiConnector(aiContext);
}

export async function deleteAiContext(id: string): Promise<boolean> {
  return deleteAiConnector(id);
}

export async function getAiParsers(): Promise<AiParser[]> {
  return callWs<AiParser[]>('getAiParsers', {});
}

export async function clearPendingProbes(): Promise<boolean> {
  return callWs<boolean>('clearPendingProbes', {});
}

export async function clearAllProbes(): Promise<boolean> {
  return callWs<boolean>('clearAllProbes', {});
}

export async function aiRefresh(): Promise<boolean> {
  return callWs<boolean>('aiRefresh', {});
}

export async function getUsageStats(): Promise<boolean> {
  return callWs<boolean>('getUsageStats', {});
}

export interface Lead {
  id: number;
  enabled: boolean;
  product: string;
  vendor: string;
  vendorLocal: string;
  title: string;
  notes: string;
  country: string;
  website: string;
  categories: string[];
};

export async function getLeads(): Promise<Lead[]> {
  return callWs<Lead[]>('getLeads', {});
}

export async function deleteLead(id: number): Promise<boolean> {
  return callWs<boolean>('deleteLead', { id });
}

export async function setLead(lead: Lead): Promise<Lead> {
  return callWs<Lead>('setLead', { lead });
}

export interface FileEntry {
  name: string;
  size: number;
  modified: number;
}

export async function listFiles(cloud: boolean, pfx: string): Promise<FileEntry[]> {
  return callWs<FileEntry[]>('listFiles', { cloud, pfx });
}

export async function deleteFile(cloud: boolean, filename: string): Promise<void> {
  return callWs<void>('deleteFile', { cloud, filename });
}

export async function getSetting(key: string): Promise<string> {
  return callWs<string>('getSetting', { key });
}
export async function setSetting(key: string, value: string, description: string): Promise<void> {
  return callWs<void>('setSetting', { key, value, description });
}

export interface SettingBulkItem {
  key: string;
  value: string;
  description?: string;
}

export interface SettingRow {
  key: string;
  val: string;
  description: string;
}

export async function getSettingsByPrefix(prefix: string): Promise<SettingRow[]> {
  return callWs<SettingRow[]>('getSettingsByPrefix', { prefix });
}

export async function setSettingsBulk(items: SettingBulkItem[]): Promise<{ changes: number }> {
  return callWs<{ changes: number }>('setSettingsBulk', { items });
}

export async function diagnosticLogBurst(): Promise<{ sent: number }> {
  return callWs<{ sent: number }>('diagnosticLogBurst', {});
}

export async function restartBackendServer(): Promise<{ ok: boolean; message?: string }> {
  return callWs<{ ok: boolean; message?: string }>('restartBackendServer', {});
}

export interface SettingEntry {
  key: string;
  val: string;
  description: string;
}

export async function exportAllSettings(): Promise<SettingEntry[]> {
  return callWs<any>('exportAllSettings', {});
}

export async function importAllSettings(allSettings: SettingEntry[]): Promise<void> {
  return callWs<void>('importAllSettings', { allSettings });
}

export interface UpdateEntry {
  name: string;
  modified: number;
  size: number;
}
export interface UpdateInfo {
  updates: UpdateEntry[];
}

export async function checkForUpdates(): Promise<UpdateInfo> {
  return callWs<UpdateInfo>('checkForUpdates', {});
}

export interface FingerprintUpdateInfo {
  available: boolean;
  latestSerial: number;
  latestFilename: string;
}

export interface UpdateNotification {
  ts: number;
  fingerprints: FingerprintUpdateInfo | null;
  firmware: UpdateInfo | null;
  errors: string[];
}

export async function checkUpdateNotifications(): Promise<UpdateNotification> {
  return callWs<UpdateNotification>('checkUpdateNotifications', {});
}

export async function downloadUpdate(updateName: string, cloud: boolean = true, progressId?: string): Promise<string> {
  return callWs<string>('downloadUpdate', { updateName, cloud, progressId });
}

export async function launchInstaller(installerPath: string): Promise<void> {
  return callWs<void>('launchInstaller', { installerPath });
}

export async function applyLinuxUpdate(updateName: string, progressId?: string): Promise<void> {
  return callWs<void>('applyLinuxUpdate', { updateName, progressId });
}

export async function exportBackup(fileName: string, cloud: boolean, metadataOverride?: AppMetadata, progressId?: string): Promise<string> {
  return callWs<string>('exportBackup', { fileName, cloud, metadataOverride, progressId });
}

export interface DiffChange {
  type: 'fingerprint' | 'category';
  op: 'add' | 'change' | 'delete';
  fp?: any;
  category?: any;
  before?: any | null;
}

export interface DiffResult {
  ok: boolean;
  hasLastOpened: boolean;
  changes: DiffChange[];
  fingerprints: number;
  categories: number;
  total: number;
}

export type DbCompareSpec =
  | { kind: 'current' }
  | { kind: 'lastLoaded' }
  | { kind: 'git'; hash: string }
  | { kind: 'backupFile'; cloud: boolean; filename: string };

export async function rebaseSnapshot(): Promise<{ ok: boolean; lastOpenedPath: string }> {
  return callWs('rebaseSnapshot', {});
}

export async function getDiff(): Promise<DiffResult> {
  return callWs<DiffResult>('getDiff', {});
}

export async function compareDbs(fromSpec: DbCompareSpec, toSpec: DbCompareSpec): Promise<DiffResult> {
  return callWs<DiffResult>('compareDbs', { fromSpec, toSpec });
}

export async function revertChange(type: string, key: string, op: string): Promise<{ ok: boolean }> {
  return callWs<{ ok: boolean }>('revertChange', { type, key, op });
}

export async function revertAll(): Promise<{ ok: boolean }> {
  return callWs<{ ok: boolean }>('revertAll', {});
}

export async function exportDiff(fileName: string, cloud: boolean, metadata?: AppMetadata, progressId?: string): Promise<{ status: string; filename: string; fingerprints: number; categories: number; total: number }> {
  return callWs('exportDiff', { fileName, cloud, metadata, progressId });
}

export async function exportGit(): Promise<string> {
  return callWs<string>('exportGit', {});
}

export async function importGit(): Promise<string> {
  return callWs<string>('importGit', {});
}

export interface GitCommitEntry {
  hash: string;
  shortHash: string;
  date: string;
  message: string;
  serial?: string;
}

export interface GitNextSerialResult {
  nextSerial: number;
  lastSerial: number | null;
}

export interface GitCommitResult {
  hash: string;
  shortHash: string;
}

export interface CommitPublishState extends GitCommitEntry {
  inPublic: boolean;
  inDev: boolean;
  devFileName: string;
}

export interface GitPublishStateResult {
  publicFiles: FileEntry[];
  devFiles: FileEntry[];
  commits: CommitPublishState[];
}

export interface PushGitToS3Result {
  processed: number;
  skipped: number;
  errors: string[];
}

export async function gitLog(): Promise<GitCommitEntry[]> {
  return callWs<GitCommitEntry[]>('gitLog', {});
}

export async function gitNextSerial(): Promise<GitNextSerialResult> {
  return callWs<GitNextSerialResult>('gitNextSerial', {});
}

export async function gitCommit(message: string, serial?: string): Promise<GitCommitResult> {
  return callWs<GitCommitResult>('gitCommit', { message, serial });
}

export async function gitCommitWithAuthor(
  message: string,
  serial: string | undefined,
  authorName: string,
  authorEmail: string,
): Promise<GitCommitResult> {
  return callWs<GitCommitResult>('gitCommit', {
    message,
    serial,
    authorName,
    authorEmail,
  });
}

export async function gitLoadCommit(hash: string): Promise<string> {
  return callWs<string>('gitLoadCommit', { hash });
}

export async function getGitPublishState(): Promise<GitPublishStateResult> {
  return callWs<GitPublishStateResult>('getGitPublishState', {});
}

export async function pushGitToS3(progressId?: string, force?: boolean): Promise<PushGitToS3Result> {
  return callWs<PushGitToS3Result>('pushGitToS3', { progressId, force });
}

export interface TerraformMachine {
  name: string;
  resourceType: string;
  instanceId: string;
  instanceType: string;
  instanceState: string;
  publicIp: string;
  privateIp: string;
  publicDns: string;
  region: string;
  endpoint: string;
  tags: Record<string, string>;
  tfvarsKey: string;
  enabled: boolean;
  ami: string;
}

export interface MachineStatus {
  connectivity: 'ok' | 'unreachable' | 'error';
  version?: string;
  memTotal?: number;
  memFree?: number;
  diskTotal?: number;
  diskFree?: number;
  lastLogins?: string[];
  lastBackup?: string;
  fetchedAt: number;
  errorDetail?: string;
}

export async function terraformListMachines(): Promise<TerraformMachine[]> {
  return callWs<TerraformMachine[]>('terraformListMachines', {});
}

export async function terraformListSiteNames(): Promise<string[]> {
  return callWs<string[]>('terraformListSiteNames', {});
}

export async function getMachineStatus(publicIp: string, tfvarsKey?: string, keyPath?: string, sshUser?: string): Promise<MachineStatus> {
  return callWs<MachineStatus>('getMachineStatus', { publicIp, tfvarsKey, keyPath, sshUser });
}

export async function terraformSetEnabled(name: string, enabled: boolean): Promise<void> {
  return callWs<void>('terraformSetEnabled', { name, enabled });
}

export type FbotBuildAction = 'bump_release' | 'build_linux_bundle' | 'upload_release' | 'first_install' | 'push_release' | 'fetch_local_db' | 'push_local';

export interface FbotBuildCommonOptions {
  target?: string;
  host?: string;
  version?: string;
  region?: string;
  bumpReleaseMode?: 'auto' | 'patch' | 'minor';
  createTag?: boolean;
  keyPath?: string;
  bundleBuildMode?: 'container' | 'native';
  bundleBuildImage?: string;
  s3Bucket?: string;
  s3LinuxPrefix?: string;
  sshUser?: string;
  suppressWeakCryptoWarning?: boolean;
  localDbBackup?: string;
}

export interface RunFbotBuildResult {
  action: FbotBuildAction;
  exitCode: number;
  stdout: string;
  stderr: string;
}

export interface BundleWorkflowInfo {
  packageVersion: string;
  latestBundleName: string;
  latestBundlePath: string;
}

export async function getBundleWorkflowInfo(): Promise<BundleWorkflowInfo> {
  return callWs<BundleWorkflowInfo>('getBundleWorkflowInfo', {});
}

export async function runFbotBuildAction(action: FbotBuildAction, options: FbotBuildCommonOptions): Promise<RunFbotBuildResult> {
  return callWs<RunFbotBuildResult>('runFbotBuildAction', { action, options });
}

export interface ImportBackupResult {
  status: string;
  message?: string;
  stack?: string;
}

export async function importBackup(fileName: string, cloud: boolean, progressId?: string): Promise<ImportBackupResult> {
  return callWs<ImportBackupResult>('importBackup', { fileName, cloud, progressId });
}

export interface AppMetadata {
  info: string;
  serial: string;
  title?: string;
  branch?: string;
  git_hash?: string;
  last_commit_msg?: string;
  appVersion: string;
  schemaVersion: string;
  supportedSchemaVersion: string;
  localSchemaVersion: string;
  supportedLocalSchemaVersion: string;
  username: string;
  user_dir: string;
  prefix: string;
  dbFileName: string;
  localDbFileName: string;
  lastFileOp?: {
    filename: string;
    cloud: string;
  };
  devMode?: boolean;
  opsMode?: boolean;
  deploymentType?: string;
}

export async function getMetadata(): Promise<AppMetadata> {
  return callWs<AppMetadata>('getMetadata', {});
}

export async function setMetadata(info: string, serial: string, title?: string, branch?: string, git_hash?: string, last_commit_msg?: string): Promise<void> {
  return callWs<void>('setMetadata', { info, serial, title, branch, git_hash, last_commit_msg });
}

export async function publish(fileName: string, cloud: boolean, metadataOverride?: AppMetadata, progressId?: string): Promise<any> {
  return callWs<any>('publish', { fileName, cloud, metadataOverride, progressId });
}

export interface MergeMetadata {
  info: string;
  serial: string;
}

export interface MergeQueueEntry {
  name: string;
  loadName?: string;
  size: number;
  modified: number;
  metadata: MergeMetadata;
  addedFingerprints: number;
  removedFingerprints: number;
  addedMatches: number;
  removedMatches: number;
  addedCategories: number;
  removedCategories: number;
  addedFpCategories: number;
  removedFpCategories: number;
  error?: string | null;
}

export interface MergePreviewRow {
  proposal_key: string;
  proposal_type: string;
  title: string;
  summary: string;
  severity: 'info' | 'warning' | 'critical';
  mode: 'add' | 'update' | 'delete' | 'merge' | string;
  pvkey: string;
  before: Partial<Fingerprint> | null;
  after: Partial<Fingerprint> | null;
}

export interface MergePreviewResult {
  ok: boolean;
  source: {
    cloud?: boolean;
    name?: string;
    prefix?: string;
    path?: string;
    fromQueue?: boolean;
  } | null;
  metadata: MergeMetadata;
  counts: {
    additions: number;
    edits: number;
    deletes: number;
    total: number;
  };
  rows: MergePreviewRow[];
  truncated: boolean;
  previewLimit: number;
}

export async function mergeImportQueue(cloud: boolean, dir: 'queue' | 'merged' | 'reject' | 'originals' | 'processed' = 'queue'): Promise<{ queue: MergeQueueEntry[] }> {
  return callWs<{ queue: MergeQueueEntry[] }>('mergeImportQueue', { cloud, dir });
}

export async function mergeLoadFile(cloud: boolean, name: string, fromQueue: boolean = false): Promise<any> {
  return callWs<any>('mergeLoadFile', { cloud, name, fromQueue });
}

export async function mergeFetchRaw(cloud: boolean, name: string, fromQueue: boolean = false): Promise<any> {
  return callWs<any>('mergeFetchRaw', { cloud, name, fromQueue });
}

export async function mergeResolveQueueItem(cloud: boolean, name: string, resolution: 'merged' | 'reject'): Promise<any> {
  return callWs<any>('mergeResolveQueueItem', { cloud, name, resolution });
}

export async function mergeMoveQueueItem(cloud: boolean, name: string, fromDir: 'queue' | 'merged' | 'reject' | 'originals' | 'processed', toDir: 'queue' | 'merged' | 'reject' | 'originals' | 'processed'): Promise<any> {
  return callWs<any>('mergeMoveQueueItem', { cloud, name, fromDir, toDir });
}

export async function mergeDeleteQueueItem(cloud: boolean, name: string, dir: 'queue' | 'merged' | 'reject' | 'originals' | 'processed'): Promise<any> {
  return callWs<any>('mergeDeleteQueueItem', { cloud, name, dir });
}

export interface PendingChange {
  id: number;
  proposal_key: string;
  proposal_type: 'add' | 'update' | 'delete' | 'merge' | string;
  fingerprint_id: number | null;
  target_fingerprint_id: number | null;
  title: string;
  summary: string;
  severity: 'info' | 'warning' | 'critical';
  status: 'todo' | 'accepted' | 'rejected';
  detail: any;
  created: number;
  updated: number;
}

export interface MergeEntry {
  id: number;
  seq: number;
  entry_type: 'fingerprint' | 'category';
  op: 'add' | 'update' | 'delete';
  status: 'created' | 'accepted' | 'rejected' | 'applied' | 'failed';
  data: any;
  error?: string | null;
  created: number;
  updated: number;
}

export interface MergeSessionInfo {
  id: number;
  source_cloud: number;
  source_name: string;
  source_dir: string;
  metadata: any;
  created: number;
}

export interface MergeSessionResult {
  session: MergeSessionInfo | null;
  entries: MergeEntry[];
}

export interface MergeCommitResult {
  ok: boolean;
  applied: number;
  failed: number;
  total: number;
  name: string;
}

export interface FuzzyVendorPair {
  id: number;
  vendor_a_norm: string;
  vendor_b_norm: string;
  similarity: number;
  source: string;
  state: 'new' | 'verified' | 'rejected' | 'applied' | 'stale';
  canonical_vendor_tag: string | null;
  canonical_vendor: string | null;
  auto_apply: number;
  notes: string | null;
  first_seen: number;
  last_seen: number;
  seen_count: number;
}

export async function mergeGetSession(): Promise<MergeSessionResult> {
  return callWs<MergeSessionResult>('mergeGetSession', {});
}

export async function mergeListEntries(): Promise<{ entries: MergeEntry[] }> {
  return callWs<{ entries: MergeEntry[] }>('mergeListEntries', {});
}

export async function mergeSetEntryStatus(id: number, status: 'created' | 'accepted' | 'rejected'): Promise<any> {
  return callWs<any>('mergeSetEntryStatus', { id, status });
}

export async function mergeSetEntryStatusAll(status: 'created' | 'accepted' | 'rejected', ids?: number[], force?: boolean): Promise<any> {
  return callWs<any>('mergeSetEntryStatusAll', { status, ids: ids || [], force: force === true });
}

export async function mergeDeleteEntry(id: number): Promise<any> {
  return callWs<any>('mergeDeleteEntry', { id });
}

export async function mergeApplyEntry(id: number): Promise<any> {
  return callWs<any>('mergeApplyEntry', { id });
}

export async function mergeCommit(softDelete = false): Promise<MergeCommitResult> {
  return callWs<MergeCommitResult>('mergeCommit', { softDelete });
}

export async function mergeAbort(): Promise<{ ok: boolean }> {
  return callWs<{ ok: boolean }>('mergeAbort', {});
}

export async function pendingChangesFuzzyPairs(state?: string): Promise<{ items: FuzzyVendorPair[] }> {
  return callWs<{ items: FuzzyVendorPair[] }>('pendingChangesFuzzyPairs', state ? { state } : {});
}

export async function pendingChangesUpdateFuzzyPair(id: number, patch: Partial<FuzzyVendorPair>): Promise<FuzzyVendorPair> {
  return callWs<FuzzyVendorPair>('pendingChangesUpdateFuzzyPair', { id, patch });
}

export interface FuzzyMatchingSettings {
  min_threshold: number;
  auto_apply_threshold: number;
  verify_threshold: number;
  short_name_max_len: number;
  short_name_high_similarity: number;
}

export interface FuzzyMatchingToken {
  id?: number | null;
  scope: string;
  token_class: string;
  token: string;
  enabled: number;
  source?: string;
  notes?: string;
}

export interface FuzzyMatchingRule {
  id?: number | null;
  scope: string;
  rule_key: string;
  action: 'auto_merge' | 'manual_review' | 'reject';
  enabled: number;
  priority: number;
  description?: string;
  config?: Record<string, any>;
}

export interface FuzzyMatchingConfig {
  settings: FuzzyMatchingSettings;
  tokens: FuzzyMatchingToken[];
  rules: FuzzyMatchingRule[];
}

export async function fuzzyMatchingGetConfig(): Promise<FuzzyMatchingConfig> {
  return callWs<FuzzyMatchingConfig>('fuzzyMatchingGetConfig', {});
}

export async function fuzzyMatchingSetConfig(config: Partial<FuzzyMatchingConfig>): Promise<FuzzyMatchingConfig> {
  return callWs<FuzzyMatchingConfig>('fuzzyMatchingSetConfig', config);
}

export interface AuthCapability {
  key: string;
  title: string;
  description: string;
}

export interface AuthRole {
  key: string;
  title: string;
  capabilities: AuthCapability[];
}

export interface AuthUser {
  id: number;
  username: string;
  dir: string;
  password_hash: string;
  created: number;
  last_login: number;
  login_failures: number;
  role: string;
}

export interface AuthActivityItem {
  id: number;
  username: string;
  event: string;
  remote_ip: string;
  details: string;
  created: number;
}

export interface AuthActivityExport {
  fileName: string;
  mime: string;
  content: string;
}

export async function authListUsers(): Promise<AuthUser[]> {
  return callWs<AuthUser[]>('authListUsers', {});
}

export async function authCreateUser(username: string, password: string, role: string, dir?: string): Promise<void> {
  return callWs<void>('authCreateUser', { username, password, role, dir });
}

export async function authDeleteUser(id: number): Promise<void> {
  return callWs<void>('authDeleteUser', { id });
}

export async function authSetUserRole(id: number, role: string): Promise<void> {
  return callWs<void>('authSetUserRole', { id, role });
}

export async function authSetUserPassword(id: number, password: string): Promise<void> {
  return callWs<void>('authSetUserPassword', { id, password });
}

export async function authSetUserDir(id: number, dir: string): Promise<void> {
  return callWs<void>('authSetUserDir', { id, dir });
}

export async function authListRoles(): Promise<AuthRole[]> {
  return callWs<AuthRole[]>('authListRoles', {});
}

export async function authListCapabilities(): Promise<AuthCapability[]> {
  return callWs<AuthCapability[]>('authListCapabilities', {});
}

export async function authUpsertRole(key: string, title: string, capabilityKeys: string[]): Promise<void> {
  return callWs<void>('authUpsertRole', { key, title, capabilityKeys });
}

export async function authDeleteRole(key: string): Promise<void> {
  return callWs<void>('authDeleteRole', { key });
}

export async function authListActivity(search: string, limit: number = 200): Promise<AuthActivityItem[]> {
  return callWs<AuthActivityItem[]>('authListActivity', { search, limit });
}

export async function authClearActivity(): Promise<void> {
  return callWs<void>('authClearActivity', {});
}

export async function authExportActivity(format: 'csv' | 'json', search: string): Promise<AuthActivityExport> {
  return callWs<AuthActivityExport>('authExportActivity', { format, search });
}

// Progress tracking

export interface ProgressSupercatEntry {
  key: string;
  finger_count: number;
  match_count: number;
  category_count: number;
}

export interface ProgressData {
  total_fingerprints: number;
  total_matches: number;
  supercats: ProgressSupercatEntry[];
  val_present: number;
  fingerprint_space_scores: number[];
  meta: Record<string, string>;
}

export interface ProgressHistoryRow {
  hash: string;
  date: string;
  message: string;
  git_tags: string;  // JSON-encoded string[]
  data: string;      // JSON-encoded ProgressData
}

export interface ProgressFailedRow {
  hash: string;
  date: string;
  message: string;
  error: string;
  tries: number;
  last_try: string;
}

export interface ProgressGitStatus {
  gitLog: GitCommitEntry[];
  history: ProgressHistoryRow[];
  failed?: ProgressFailedRow[];
}

export interface UpdateProgressResult {
  processed: number;
  skipped: number;
  errors: number;
  remaining: number;
  total: number;
}

export interface UpdateProgressOpts {
  force?: boolean;
  limit?: number;       // commits per call; 0 means no cap
  retryFailed?: boolean;
}

export async function getProgressHistory(): Promise<ProgressHistoryRow[]> {
  return callWs<ProgressHistoryRow[]>('getProgressHistory', {});
}

export async function getProgressGitStatus(): Promise<ProgressGitStatus> {
  return callWs<ProgressGitStatus>('getProgressGitStatus', {});
}

export async function updateProgressFromGit(opts: UpdateProgressOpts = {}): Promise<UpdateProgressResult> {
  return callWs<UpdateProgressResult>('updateProgressFromGit', {
    force: opts.force === true,
    retryFailed: opts.retryFailed === true,
    ...(opts.limit === undefined ? {} : { limit: opts.limit }),
  });
}

/*
 * Drive updateProgressFromGit in batches until nothing is left to snapshot.
 * Each call handles a few commits, so onProgress can report live counts and
 * shouldStop can abandon the run between batches without losing the commits
 * already cached on the server.
 */
export async function updateProgressBatched(
  opts: UpdateProgressOpts & {
    onProgress?: (done: number, todo: number, r: UpdateProgressResult) => void;
    shouldStop?: () => boolean;
  } = {}
): Promise<UpdateProgressResult> {
  const { onProgress, shouldStop, ...callOpts } = opts
  const totals: UpdateProgressResult = { processed: 0, skipped: 0, errors: 0, remaining: 0, total: 0 }
  let first = true

  for (;;) {
    // force/retryFailed apply to the first batch only: force invalidates the
    // cache once, and re-trying failures once per run keeps a permanently
    // broken commit from being retried in every batch.
    const r = await updateProgressFromGit(
      first ? callOpts : { ...callOpts, force: false, retryFailed: false }
    )
    first = false
    totals.processed += r.processed
    totals.errors += r.errors
    totals.skipped = r.skipped
    totals.remaining = r.remaining
    totals.total = r.total
    const done = totals.processed + totals.errors
    onProgress?.(done, done + r.remaining, r)
    if (r.remaining <= 0) {
      break
    }
    // A batch that snapshots nothing means no forward progress is possible.
    if (r.processed === 0 && r.errors === 0) {
      break
    }
    if (shouldStop?.()) {
      break
    }
  }
  return totals
}

export async function publishProgressToS3(): Promise<string> {
  return callWs<string>('publishProgressToS3', {});
}

export async function pullProgressFromS3(fullReplace: boolean = false): Promise<number> {
  return callWs<number>('pullProgressFromS3', { fullReplace });
}